import { useState, useCallback } from "react";
import QboxmailApi from "../qboxmailApi";

const api = new QboxmailApi({ useCookie: true });
/**
 * Hook for performing API mutations with optional optimistic updates and pagination support.
 *
 * @param {string} apiMethod – Name of the QboxmailApi method to call.
 * @param {Object} [options] – Configuration options.
 * @param {(any, ...any) => (function|void)} [options.optimistic] – Function invoked before the API call for optimistic updates. Receives the first argument (e.g., id) and additional args, and may return a rollback function.
 * @returns {{
 *   pendingItems: any[],
 *   error: Error|null,
 *   mutate: function(any, ...any): Promise<any>
 *   isLoading: boolean
 * }} Hook API: array of pending item keys, last error, and mutate function.
 */
export function useApiMutation(apiMethod, options = {}) {
  const { optimistic } = options;
  const [pendingItems, setPendingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(
    async (id, ...args) => {
      setPendingItems((old) => [...old, id]);
      setIsLoading(true);
      setError(null);
      let rollback;
      if (typeof optimistic === "function") {
        rollback = optimistic(id, ...args);
      }
      try {
        const response = await api[apiMethod](id, ...args);
        return response;
      } catch (err) {
        setError(err);
        if (typeof rollback === "function") {
          rollback();
        }
        throw err;
      } finally {
        setIsLoading(false);
        setPendingItems((old) => old.filter((lid) => lid !== id));
      }
    },
    [apiMethod, optimistic]
  );

  return { pendingItems, error, mutate, isLoading };
}
